import React, { useState, useEffect } from 'react';
import { useUser } from '@/contexts/UserContext';
import { apiRequest } from '@/services/queryClient';
import { getApiBaseUrl } from '@/config/production';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShoppingCart, Download, Loader2, AlertCircle, Package, Eye, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'wouter';
import { PhotoPreviewModal } from '@/components/photo-preview-modal';

interface Photo {
  id: string;
  filename: string;
  original_filename: string;
  storageurl: string;
  photographer?: {
    id: string;
    name: string;
    email: string;
  };
  photo_events?: Array<{
    event: {
      title: string;
    };
  }>;
}

interface OrderItem {
  id: string;
  photoid: string;
  quantity: number;
  unitprice: string;
  photo?: Photo;
  event?: {
    title: string;
    author: {
      name: string;
      email: string;
    };
  };
}

interface Payment {
  id: string;
  provider: string;
  amount: string;
  status: string;
  providerreference: string;
}

interface Order {
  id: string;
  userid: string;
  eventid: string | null;
  totalamount: string;
  status: string;
  createdat: string;
  ordernumber: number;
  items: OrderItem[];
  payments: Payment[];
}

export default function OrdersPage() {
  const { user, isLoggedIn, isLoading: isUserLoading } = useUser();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [previewPhoto, setPreviewPhoto] = useState<any>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const API_BASE = `${getApiBaseUrl()}/api`;
      const response = await apiRequest('GET', `${API_BASE}/orders?userId=${user?.id}`);
      const data = await response.json();

      if (data.success) {
        setOrders(data.data || []);
      } else {
        setError('Erro ao carregar pedidos');
      }
    } catch (err) {
      console.error('Erro ao buscar pedidos:', err);
      setError('Erro ao carregar pedidos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (photoId: string, filename: string) => {
    try {
      const API_BASE = `${getApiBaseUrl()}/api`;
      const downloadUrl = `${API_BASE}/storage/download/${photoId}?userId=${user?.id}`;

      // Abrir download em nova aba
      window.open(downloadUrl, '_blank');
    } catch (err) {
      console.error('Erro ao fazer download:', err);
      alert('Erro ao fazer download da foto');
    }
  };

  const handleViewPhoto = (photo: any) => {
    setPreviewPhoto(photo);
    setIsPreviewOpen(true);
  };

  const handleDownloadFromPreview = (photo: any) => {
    handleDownload(photo.id, photo.original_filename || 'foto.jpg');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Paid': return 'bg-primary/10 text-primary border-primary/20';
      case 'Pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };


  useEffect(() => {
    if (isLoggedIn && user?.id) {
      fetchOrders();
    } else if (!isUserLoading) {
      setIsLoading(false);
      setError('Você precisa estar logado para ver seus pedidos.');
    }
  }, [isLoggedIn, user?.id, isUserLoading]);

  if (!isLoggedIn) {
    return (
      <div className="container mx-auto px-4 py-8">

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Acesso Negado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p>Por favor, faça login para visualizar seus pedidos.</p>
            <Button asChild className="mt-4">
              <Link to="/login">Fazer Login</Link>
            </Button>
          </CardContent>
        </Card>
      </div>

    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
        <p className="mt-2">Carregando pedidos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Erro
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p>{error}</p>
            <Button onClick={fetchOrders} className="mt-4">
              Tentar Novamente
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Meus Pedidos
        </h1>
        <p className="text-gray-600">
          Acompanhe o status dos seus pedidos e baixe suas fotos
        </p>
      </div>


      {orders.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <ShoppingCart className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-lg text-gray-600">Você ainda não fez nenhum pedido.</p>
            <Button asChild className="mt-4">
              <Link to="/">Explorar Fotos</Link>
            </Button>

          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <Card key={order.id} className="border-l-4 border-l-green-500">
              {/* Cabeçalho do Pedido */}
              <div className="flex items-center justify-between p-4 border-b bg-gray-50">
                <div className="flex items-center gap-3">
                  <Package className="h-5 w-5 text-primary" />
                  <div>

                    <h3 className="font-semibold text-lg">
                      <Link to={`/order-status/${order.id}`} className="hover:text-primary transition-colors">
                        Pedido #{order.ordernumber || order.id.substring(0, 8)}
                      </Link>
                    </h3>
                    <p className="text-sm text-primary">
                      {format(new Date(order.createdat), 'dd/MM/yyyy HH:mm')}
                    </p>
                  </div>

                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <span className="text-sm text-muted-foreground">Status: </span>
                    <Badge className={getStatusColor(order.status)}>
                      {order.status === 'Paid' ? 'Pago' : order.status === 'Pending' ? 'Pendente' : 'Cancelado'}
                    </Badge>
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-muted-foreground">Valor: </span>
                    <span className="font-semibold text-lg">R$ {parseFloat(order.totalamount).toFixed(2)}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      asChild
                      className="flex items-center gap-1"
                    >
                      <Link to={`/order-status/${order.id}`}>
                        <FileText className="h-3 w-3" />
                        <span className="text-xs">Status do Pedido</span>
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>


              {/* Itens do Pedido */}
              <div className="p-4">
                <div className="space-y-2">
                  {order.items.map((item, index) => (
                    <div key={item.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                      <div className="flex items-center gap-3 flex-1">
                        <span className="text-sm text-gray-500 w-8">#{index + 1}</span>

                        {/* Foto à esquerda */}
                        <div className="flex-shrink-0">
                          <img
                            src={item.photo?.storageurl}
                            alt={item.photo?.original_filename || `Foto ${item.photoid.slice(-8)}`}
                            className="w-12 h-12 object-cover rounded-lg"
                          />
                        </div>

                        {/* Informações à direita */}
                        <div className="flex-1">
                          <p className="font-medium text-sm">
                            {item.photo?.original_filename || `Foto ${item.photoid.slice(-8)}`}
                          </p>
                          <p className="text-xs text-primary">
                            Evento: {item.event?.title || 'Não especificado'}
                          </p>
                          <p className="text-xs text-primary">
                            Fotógrafo: {item.event?.author?.name || 'Não especificado'}
                          </p>
                        </div>

                        <div className="text-right">
                          <p className="text-sm font-medium">
                            R$ {parseFloat(item.unitprice).toFixed(2)}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewPhoto(item.photo)}
                          className="flex items-center gap-1"
                        >
                          <Eye className="h-3 w-3" />
                          <span className="text-xs">Visualizar</span>
                        </Button>

                        {order.status === 'Paid' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownload(item.photoid, item.photo?.original_filename || 'foto.jpg')}
                            className="flex items-center gap-1"
                          >
                            <Download className="h-3 w-3" />
                            <span className="text-xs">Download</span>
                          </Button>
                        )}
                      </div>
                    </div>

                  ))}
                </div>
              </div>

            </Card>
          ))}
        </div>
      )}

      {/* Modal de Visualização da Foto */}
      <PhotoPreviewModal
        photo={previewPhoto}
        isOpen={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewPhoto(null);
        }}
        onDownload={handleDownloadFromPreview}
      />
    </div>
  );

}